import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

// ── Ícones ──────────────────────────────────────────────────────────────────
const ICONS = {
  plus:     'M12 4v16m8-8H4',
  edit:     'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  trash:    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  save:     'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',
  times:    'M6 18L18 6M6 6l12 12',
  question: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  list:     'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  tag:      'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a2 2 0 012-2z',
  text:     'M4 6h16M4 12h16M4 18h7',
  yn:       'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  multi:    'M4 6h16M4 10h16M4 14h16M4 18h16',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
};

const SvgIcon = ({ d, size = 14, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

// ── Estilos base ─────────────────────────────────────────────────────────────
const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};
const Label = ({ children }) => (
  <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '5px' }}>
    {children}
  </label>
);

// ── Badges de tipo de pergunta ───────────────────────────────────────────────
const TIPO_PERGUNTA_META = {
  texto_livre:      { label: 'Texto Livre',      icon: ICONS.text,  bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  booleano:         { label: 'Sim / Não',         icon: ICONS.yn,    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  multipla_escolha: { label: 'Múltipla Escolha', icon: ICONS.multi, bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
};
const TipoBadge = ({ tipo }) => {
  const meta = TIPO_PERGUNTA_META[tipo] || TIPO_PERGUNTA_META.texto_livre;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
      <SvgIcon d={meta.icon} size={10} color={meta.color} />
      {meta.label}
    </span>
  );
};

// ── Paleta de cores para os tipos de ficha ───────────────────────────────────
const COR_PALETTE = [
  '#2563eb', '#16a34a', '#9333ea', '#dc2626', '#d97706',
  '#0891b2', '#be185d', '#059669', '#7c3aed', '#b45309',
];

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Gerenciar Tipos de Ficha
// ════════════════════════════════════════════════════════════════════════════
const TipoFichaModal = ({ show, onClose, tipos, onTipoSaved, onTipoDeleted, entidadeId }) => {
  const { showAlertModal } = useAlertModal();
  const [novoNome, setNovoNome]   = useState('');
  const [novaCor, setNovaCor]     = useState('#2563eb');
  const [isSaving, setIsSaving]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editNome, setEditNome]   = useState('');
  const [editCor, setEditCor]     = useState('');

  useEffect(() => {
    if (!show) { setNovoNome(''); setNovaCor('#2563eb'); setEditingId(null); }
  }, [show]);

  const handleCreate = async () => {
    if (!novoNome.trim()) return;
    setIsSaving(true);
    try {
      const res = await axios.post('/api/ficha-anamnese-tipos', {
        nome: novoNome.trim(), cor: novaCor, entidade_id: entidadeId,
      });
      onTipoSaved(res.data.tipo);
      setNovoNome(''); setNovaCor('#2563eb');
      showAlertModal('Sucesso', 'Tipo criado!', 'success');
    } catch (err) {
      showAlertModal('Erro', err.response?.data?.message || 'Falha ao criar tipo.', 'danger');
    } finally { setIsSaving(false); }
  };

  const handleUpdate = async (id) => {
    setIsSaving(true);
    try {
      const res = await axios.put(`/api/ficha-anamnese-tipos/${id}`, { nome: editNome, cor: editCor });
      onTipoSaved(res.data.tipo);
      setEditingId(null);
      showAlertModal('Sucesso', 'Tipo atualizado!', 'success');
    } catch (err) {
      showAlertModal('Erro', err.response?.data?.message || 'Falha ao atualizar.', 'danger');
    } finally { setIsSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este tipo de ficha?')) return;
    try {
      await axios.delete(`/api/ficha-anamnese-tipos/${id}`);
      onTipoDeleted(id);
      showAlertModal('Sucesso', 'Tipo removido.', 'success');
    } catch (err) {
      showAlertModal('Erro', err.response?.data?.message || 'Não foi possível excluir.', 'danger');
    }
  };

  const startEdit = (tipo) => {
    setEditingId(tipo.id);
    setEditNome(tipo.nome);
    setEditCor(tipo.cor || '#2563eb');
  };

  if (!show) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,.5)', backdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'relative', backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px', boxShadow: '0 25px 60px rgba(0,0,0,.2)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon d={ICONS.tag} size={15} color="#fff" />
            </div>
            <span style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '800', color: '#fff' }}>
              Tipos de Ficha de Anamnese
            </span>
          </div>
          <button type="button" onClick={onClose} style={{ width: '28px', height: '28px', borderRadius: '7px', border: 'none', backgroundColor: 'rgba(255,255,255,.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SvgIcon d={ICONS.times} size={14} color="#fff" />
          </button>
        </div>

        <div style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>

          {/* Criar novo tipo */}
          <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '14px', marginBottom: '20px' }}>
            <Label>Novo Tipo de Ficha</Label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text" value={novoNome}
                onChange={e => setNovoNome(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                placeholder="Ex: CO2, Capilar, Estética..."
                style={{ ...inputBase, flex: 1 }}
                {...fh}
              />
              {/* Seletor de cor */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <input
                  type="color" value={novaCor}
                  onChange={e => setNovaCor(e.target.value)}
                  style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer', padding: '2px' }}
                  title="Escolher cor"
                />
              </div>
              <button
                type="button" onClick={handleCreate} disabled={isSaving || !novoNome.trim()}
                style={{ padding: '9px 14px', borderRadius: '8px', border: 'none', background: (!novoNome.trim() || isSaving) ? '#e5e7eb' : 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: (!novoNome.trim() || isSaving) ? '#9ca3af' : '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', cursor: (!novoNome.trim() || isSaving) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
              >
                {isSaving ? <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} /> : '+ Criar'}
              </button>
            </div>
            {/* Paleta rápida */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
              {COR_PALETTE.map(cor => (
                <button
                  key={cor} type="button"
                  onClick={() => setNovaCor(cor)}
                  style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: cor, border: novaCor === cor ? '3px solid #111' : '2px solid transparent', cursor: 'pointer', padding: 0 }}
                  title={cor}
                />
              ))}
            </div>
          </div>

          {/* Lista de tipos existentes */}
          <Label>Tipos Cadastrados ({tipos.length})</Label>
          {tipos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: `calc(12px * var(--font-scale))` }}>
              Nenhum tipo cadastrado ainda
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {tipos.map(tipo => (
                <div key={tipo.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fff', border: '1px solid #e5e7eb' }}>
                  {editingId === tipo.id ? (
                    <>
                      <input
                        type="color" value={editCor}
                        onChange={e => setEditCor(e.target.value)}
                        style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: 'pointer', padding: '2px', flexShrink: 0 }}
                      />
                      <input
                        type="text" value={editNome}
                        onChange={e => setEditNome(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleUpdate(tipo.id)}
                        style={{ ...inputBase, flex: 1, padding: '6px 10px' }}
                        autoFocus
                      />
                      <button type="button" onClick={() => handleUpdate(tipo.id)} disabled={isSaving}
                        style={{ padding: '5px 10px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
                        ✓
                      </button>
                      <button type="button" onClick={() => setEditingId(null)}
                        style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: tipo.cor || '#2563eb', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                        {tipo.nome}
                      </span>
                      <button type="button" onClick={() => startEdit(tipo)}
                        style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SvgIcon d={ICONS.edit} size={12} color="#2563eb" />
                      </button>
                      <button type="button" onClick={() => handleDelete(tipo.id)}
                        style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SvgIcon d={ICONS.trash} size={12} color="#dc2626" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Criar / Editar Pergunta
// ════════════════════════════════════════════════════════════════════════════
const AnamneseModal = ({ show, onClose, onSave, currentQuestion, isSaving, tipos }) => {
  const [formData, setFormData] = useState({
    tipo_ficha_id: '', tipo_ficha: '', pergunta: '', tipo_pergunta: 'texto_livre', opcoes: [],
  });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (!show) return;
    if (currentQuestion) {
      setFormData({
        tipo_ficha_id: currentQuestion.tipo_ficha_id || '',
        tipo_ficha:    currentQuestion.tipo_ficha    || '',
        pergunta:      currentQuestion.pergunta,
        tipo_pergunta: currentQuestion.tipo_pergunta || 'texto_livre',
        opcoes:        Array.isArray(currentQuestion.opcoes) ? currentQuestion.opcoes : [],
      });
    } else {
      const primeiro = tipos[0];
      setFormData({
        tipo_ficha_id: primeiro?.id   || '',
        tipo_ficha:    primeiro?.nome || '',
        pergunta: '', tipo_pergunta: 'texto_livre', opcoes: [],
      });
    }
    setNewOption('');
  }, [show, currentQuestion, tipos]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'tipo_ficha_id') {
      const tipo = tipos.find(t => String(t.id) === value);
      setFormData(prev => ({ ...prev, tipo_ficha_id: value, tipo_ficha: tipo?.nome || '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData(prev => ({ ...prev, opcoes: [...prev.opcoes, newOption.trim()] }));
      setNewOption('');
    }
  };
  const handleOptionKeyDown = e => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } };
  const handleRemoveOption  = idx => setFormData(prev => ({ ...prev, opcoes: prev.opcoes.filter((_, i) => i !== idx) }));

  if (!show) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,.5)', backdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'relative', backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '580px', boxShadow: '0 25px 60px rgba(0,0,0,.2)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon d={ICONS.question} size={16} color="#fff" />
            </div>
            <span style={{ fontSize: `calc(15px * var(--font-scale))`, fontWeight: '800', color: '#fff' }}>
              {currentQuestion ? 'Editar Pergunta' : 'Nova Pergunta'}
            </span>
          </div>
          <button type="button" onClick={onClose} style={{ width: '28px', height: '28px', borderRadius: '7px', border: 'none', backgroundColor: 'rgba(255,255,255,.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SvgIcon d={ICONS.times} size={14} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {tipos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', backgroundColor: '#fef9c3', borderRadius: '10px', border: '1px solid #fde68a', color: '#92400e', fontSize: `calc(13px * var(--font-scale))` }}>
              ⚠️ Nenhum tipo de ficha cadastrado. Crie um tipo antes de adicionar perguntas.
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                {/* Tipo de Ficha — dinâmico */}
                <div>
                  <Label>Tipo de Ficha</Label>
                  <select name="tipo_ficha_id" value={formData.tipo_ficha_id} onChange={handleChange} style={{ ...inputBase }} {...fh}>
                    {tipos.map(t => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Tipo de Resposta</Label>
                  <select name="tipo_pergunta" value={formData.tipo_pergunta} onChange={handleChange} style={{ ...inputBase }} {...fh}>
                    <option value="texto_livre">Texto Livre</option>
                    <option value="booleano">Sim / Não</option>
                    <option value="multipla_escolha">Múltipla Escolha</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Label>Enunciado da Pergunta</Label>
                <textarea
                  name="pergunta" value={formData.pergunta} onChange={handleChange}
                  rows={3} style={{ ...inputBase, resize: 'vertical' }}
                  placeholder="Digite a pergunta que será exibida na ficha de anamnese..."
                  {...fh}
                />
              </div>

              {formData.tipo_pergunta === 'multipla_escolha' && (
                <div style={{ backgroundColor: '#fdf4ff', borderRadius: '10px', border: '1px solid #e9d5ff', padding: '14px' }}>
                  <Label>Opções do Menu</Label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="text" value={newOption}
                      onChange={e => setNewOption(e.target.value)}
                      onKeyDown={handleOptionKeyDown}
                      placeholder="Ex: Hipertenso — pressione Enter para adicionar"
                      style={{ ...inputBase, flex: 1 }}
                      {...fh}
                    />
                    <button type="button" onClick={handleAddOption}
                      style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                      + Adicionar
                    </button>
                  </div>
                  {formData.opcoes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '12px', color: '#a855f7', fontSize: `calc(12px * var(--font-scale))` }}>
                      Nenhuma opção adicionada ainda
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {formData.opcoes.map((opt, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#fff', border: '1px solid #d8b4fe', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#7c3aed' }}>
                          {opt}
                          <button type="button" onClick={() => handleRemoveOption(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}>
                            <SvgIcon d={ICONS.times} size={10} color="#dc2626" stroke={2.5} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#f8fafc' }}>
          <button type="button" onClick={onClose} disabled={isSaving}
            style={{ padding: '9px 18px', borderRadius: '9px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button type="button" onClick={() => onSave(formData)} disabled={isSaving || tipos.length === 0}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '9px', border: 'none', background: (isSaving || tipos.length === 0) ? '#e5e7eb' : 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: (isSaving || tipos.length === 0) ? '#9ca3af' : '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', cursor: (isSaving || tipos.length === 0) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} /> Salvando...</>
            ) : (
              <><SvgIcon d={ICONS.save} size={13} color="#fff" /> Salvar</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
const AnamneseTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal }  = useAlertModal();

  // ── Estado: tipos de ficha ──
  const [tipos, setTipos]                   = useState([]);
  const [isLoadingTipos, setIsLoadingTipos] = useState(false);
  const [showTipoModal, setShowTipoModal]   = useState(false);

  // ── Estado: perguntas ──
  const [questions, setQuestions]             = useState([]);
  const [isLoading, setIsLoading]             = useState(false);
  const [showModal, setShowModal]             = useState(false);
  const [isSaving, setIsSaving]               = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  // ── Filtro por tipo ──
  const [filtroTipoId, setFiltroTipoId] = useState('todos');

  // ── Buscar tipos ──
  const fetchTipos = useCallback(async () => {
    if (!currentEntidade) return;
    setIsLoadingTipos(true);
    try {
      const res = await axios.get(`/api/ficha-anamnese-tipos?entidade_id=${currentEntidade.id}`);
      setTipos(res.data.tipos || []);
    } catch {
      showAlertModal('Erro', 'Não foi possível carregar os tipos de ficha.', 'danger');
    } finally {
      setIsLoadingTipos(false);
    }
  }, [currentEntidade, showAlertModal]);

  // ── Buscar perguntas ──
  const fetchQuestions = useCallback(async () => {
  if (!currentEntidade) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`/api/panamneses?entidade_id=${currentEntidade.id}`);

      // ← Normaliza: se tipo_ficha vier como objeto, extrai nome e cor
      const normalized = (res.data || []).map(q => {
        if (q.tipo_ficha && typeof q.tipo_ficha === 'object') {
          return {
            ...q,
            tipo_ficha_cor: q.tipo_ficha.cor  || '#2563eb',
            tipo_ficha:     q.tipo_ficha.nome || '—',
          };
        }
        // Registro antigo sem tipo_ficha_id: busca a cor nos tipos já carregados
        return {
          ...q,
          tipo_ficha_cor: null, // será resolvido por corDoTipo
        };
      });

      setQuestions(normalized);
    } catch {
      showAlertModal('Erro', 'Não foi possível carregar as perguntas de anamnese.', 'danger');
    } finally {
      setIsLoading(false);
    }
  }, [currentEntidade, showAlertModal]);

  useEffect(() => { fetchTipos(); fetchQuestions(); }, [fetchTipos, fetchQuestions]);

  // ── Callbacks do TipoFichaModal ──
  const handleTipoSaved = (tipo) => {
    setTipos(prev => {
      const idx = prev.findIndex(t => t.id === tipo.id);
      return idx >= 0 ? prev.map(t => t.id === tipo.id ? tipo : t) : [...prev, tipo];
    });
  };
  const handleTipoDeleted = (id) => {
    setTipos(prev => prev.filter(t => t.id !== id));
    if (String(filtroTipoId) === String(id)) setFiltroTipoId('todos');
  };

  // ── Salvar pergunta ──
  const handleSave = async (formData) => {
    if (!formData.pergunta.trim()) {
      showAlertModal('Atenção', 'A pergunta não pode estar vazia.', 'warning');
      return;
    }
    if (!formData.tipo_ficha_id) {
      showAlertModal('Atenção', 'Selecione um tipo de ficha.', 'warning');
      return;
    }
    if (formData.tipo_pergunta === 'multipla_escolha' && formData.opcoes.length === 0) {
      showAlertModal('Atenção', 'Múltipla escolha exige pelo menos uma opção.', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        pergunta:      formData.pergunta,
        tipo_pergunta: formData.tipo_pergunta,
        opcoes:        formData.opcoes,
        tipo_ficha:    formData.tipo_ficha,
        tipo_ficha_id: formData.tipo_ficha_id,
        entidade_id:   currentEntidade.id,
      };
      if (currentQuestion) {
        await axios.put(`/api/panamneses/${currentQuestion.id}`, payload);
        showAlertModal('Sucesso', 'Pergunta atualizada!', 'success');
      } else {
        await axios.post('/api/panamneses', payload);
        showAlertModal('Sucesso', 'Pergunta criada!', 'success');
      }
      setShowModal(false);
      fetchQuestions();
    } catch {
      showAlertModal('Erro', 'Falha ao salvar a pergunta.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta pergunta? Isso não afetará anamneses já preenchidas.')) return;
    try {
      await axios.delete(`/api/panamneses/${id}`);
      showAlertModal('Sucesso', 'Pergunta removida.', 'success');
      fetchQuestions();
    } catch {
      showAlertModal('Erro', 'Não foi possível excluir a pergunta.', 'danger');
    }
  };

  // ── Perguntas filtradas ──
  const questionsFiltradas = filtroTipoId === 'todos'
    ? questions
    : questions.filter(q => String(q.tipo_ficha_id) === String(filtroTipoId));

  // ── Cor do badge de ficha ──
  const corDoTipo = (tipoFichaId) => {
    const tipo = tipos.find(t => String(t.id) === String(tipoFichaId));
    return tipo?.cor || '#2563eb';
  };

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      {/* Modal de tipos */}
      <TipoFichaModal
        show={showTipoModal}
        onClose={() => setShowTipoModal(false)}
        tipos={tipos}
        onTipoSaved={handleTipoSaved}
        onTipoDeleted={handleTipoDeleted}
        entidadeId={currentEntidade?.id}
      />

      {/* Modal de pergunta */}
      <AnamneseModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        currentQuestion={currentQuestion}
        isSaving={isSaving}
        tipos={tipos}
      />

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SvgIcon d={ICONS.list} size={22} color="#fff" stroke={1.8} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Perguntas de Anamnese</div>
            <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>
              {questions.length} {questions.length === 1 ? 'pergunta' : 'perguntas'} • {tipos.length} {tipos.length === 1 ? 'tipo de ficha' : 'tipos de ficha'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Botão gerenciar tipos */}
          <button
            type="button" onClick={() => setShowTipoModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,.3)', backgroundColor: 'rgba(255,255,255,.1)', color: '#fff', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <SvgIcon d={ICONS.settings} size={13} color="#fff" />
            Tipos de Ficha
          </button>
          {/* Botão nova pergunta */}
          <button
            type="button" onClick={() => { setCurrentQuestion(null); setShowModal(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 18px', borderRadius: '10px', border: 'none', backgroundColor: 'rgba(255,255,255,.15)', color: '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', backdropFilter: 'blur(4px)' }}
          >
            <SvgIcon d={ICONS.plus} size={14} color="#fff" stroke={2.5} />
            Nova Pergunta
          </button>
        </div>
      </div>

      {/* ── FILTROS POR TIPO ── */}
      {tipos.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button
            type="button" onClick={() => setFiltroTipoId('todos')}
            style={{ padding: '5px 14px', borderRadius: '999px', border: 'none', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: filtroTipoId === 'todos' ? '#1e3a5f' : '#f1f5f9', color: filtroTipoId === 'todos' ? '#fff' : '#6b7280', transition: 'all .15s' }}
          >
            Todos ({questions.length})
          </button>
          {tipos.map(tipo => {
            const count = questions.filter(q => String(q.tipo_ficha_id) === String(tipo.id)).length;
            const ativo = String(filtroTipoId) === String(tipo.id);
            return (
              <button
                key={tipo.id} type="button"
                onClick={() => setFiltroTipoId(tipo.id)}
                style={{ padding: '5px 14px', borderRadius: '999px', border: 'none', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: ativo ? (tipo.cor || '#2563eb') : '#f1f5f9', color: ativo ? '#fff' : '#6b7280', transition: 'all .15s' }}
              >
                {tipo.nome} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* ── CONTEÚDO ── */}
      <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>

        {/* Aviso: sem tipos cadastrados */}
        {!isLoadingTipos && tipos.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: '12px', backgroundColor: '#fef9c3', borderBottom: '1px solid #fde68a' }}>
            <div style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#92400e', fontWeight: '600' }}>
              ⚠️ Nenhum tipo de ficha cadastrado
            </div>
            <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#a16207' }}>
              Clique em "Tipos de Ficha" no cabeçalho para criar o primeiro tipo (ex: Médico, CO2, Capilar).
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
            <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
            <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando perguntas...</span>
          </div>
        ) : questionsFiltradas.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 20px', gap: '12px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon d={ICONS.question} size={28} color="#94a3b8" stroke={1.5} />
            </div>
            <div style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#374151' }}>
              {filtroTipoId === 'todos' ? 'Nenhuma pergunta cadastrada' : 'Nenhuma pergunta neste tipo'}
            </div>
            <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af' }}>
              Clique em "Nova Pergunta" para começar
            </div>
          </div>
        ) : (
          <>
            {/* Cabeçalho da tabela */}
            <div style={{ display: 'grid', gridTemplateColumns: '130px 160px 1fr 90px', padding: '10px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {['Ficha', 'Tipo', 'Pergunta', 'Ações'].map((h, i) => (
                <div key={h} style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', textAlign: i === 3 ? 'center' : 'left' }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Linhas */}
            {questionsFiltradas.map((q, idx) => {
              const cor = corDoTipo(q.tipo_ficha_id);
              // cor com opacidade para o fundo do badge
              const bgHex = cor + '20';
              return (
                <div
                  key={q.id}
                  style={{ display: 'grid', gridTemplateColumns: '130px 160px 1fr 90px', padding: '14px 20px', alignItems: 'center', borderBottom: idx < questionsFiltradas.length - 1 ? '1px solid #f1f5f9' : 'none', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f7ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#fafafa'; }}
                >
                  {/* Ficha — badge com cor dinâmica */}
                  <div>
                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', padding: '3px 9px', borderRadius: '999px', backgroundColor: bgHex, color: cor, border: `1px solid ${cor}40` }}>
                      {q.tipo_ficha}
                    </span>
                  </div>

                  {/* Tipo de resposta */}
                  <div><TipoBadge tipo={q.tipo_pergunta} /></div>

                  {/* Pergunta */}
                  <div style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#374151', lineHeight: '1.5', paddingRight: '16px' }}>
                    {q.pergunta}
                    {q.tipo_pergunta === 'multipla_escolha' && Array.isArray(q.opcoes) && q.opcoes.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                        {q.opcoes.slice(0, 4).map((opt, i) => (
                          <span key={i} style={{ fontSize: `calc(10px * var(--font-scale))`, padding: '1px 7px', borderRadius: '999px', backgroundColor: '#fdf4ff', color: '#9333ea', border: '1px solid #e9d5ff' }}>
                            {opt}
                          </span>
                        ))}
                        {q.opcoes.length > 4 && (
                          <span style={{ fontSize: `calc(10px * var(--font-scale))`, padding: '1px 7px', borderRadius: '999px', backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                            +{q.opcoes.length - 4} mais
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Ações */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <button type="button" onClick={() => { setCurrentQuestion(q); setShowModal(true); }} title="Editar"
                      style={{ width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.querySelector('svg').style.stroke = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.querySelector('svg').style.stroke = '#2563eb'; }}>
                      <SvgIcon d={ICONS.edit} size={13} color="#2563eb" />
                    </button>
                    <button type="button" onClick={() => handleDelete(q.id)} title="Excluir"
                      style={{ width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.querySelector('svg').style.stroke = '#fff'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; e.currentTarget.querySelector('svg').style.stroke = '#dc2626'; }}>
                      <SvgIcon d={ICONS.trash} size={13} color="#dc2626" />
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AnamneseTab;
